import StrugglingToConnectSvg from '@forwardflow/assets/svg/StrugglingToConnectSvg'
import TeamPerformanceSvg from '@forwardflow/assets/svg/TeamPerformanceSvg'
import { useGetDashboardChartsOverview } from '@forwardflow/hooks/customHooks'
import { TimePeriod } from '@forwardflow/types'
import { Skeleton } from 'antd'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

const TeamPerformanceCard = () => {
   const [searchParams] = useSearchParams()
   const timePeriod = searchParams.get('timePeriod') || 'allTime'
   const {
      data: DashboardChartsOverview,
      isLoading: DashboardChartsOverviewLoading,
   } = useGetDashboardChartsOverview({
      timePeriod: timePeriod as TimePeriod,
   })

   const [selectedTab, setSelectedTab] = useState('yourTeam')

   if (DashboardChartsOverviewLoading) {
      return (
         <div>
            <Skeleton active paragraph={{ rows: 6 }} />
         </div>
      )
   }

   const teamPerformance = DashboardChartsOverview?.teamPerformance
   const currentTab = teamPerformance?.tabs?.find(
      (tab) => tab.id === selectedTab
   )
   const topCloser = currentTab?.highlights?.find((h) => h.id === 'topCloser')
   const strugglingToConnect = currentTab?.highlights?.find(
      (h) => h.id === 'strugglingToConnect'
   )

   const EmptyForwardFlowCard = () => (
      <div className="flex flex-col items-center justify-center w-full h-full p-8">
         <img
            src="./images/empty.png"
            alt="No Forward Flow Data"
            className="w-24 h-24 mb-5"
         />
         <div className="text-sm text-center text-white/70">
            No Forward Flow data available yet
         </div>
         <div className="mt-2 text-sm text-center text-white/70">
            <Link to="/campaign" className="text-[#A78BFA] font-medium">
               Deploy your first campaign
            </Link>{' '}
            to start tracking automated performance here.
         </div>
      </div>
   )

   return (
      <div className="flex flex-col w-full h-full mx-auto overflow-hidden bg-white/5 rounded-xl">
         <div className="flex flex-col items-start justify-between gap-4 p-4 border-b-2 md:flex-row md:items-center md:px-6 md:py-0 shrink-0 h-14 border-black/50 md:gap-0">
            <span className="text-base font-medium text-gray-300 md:text-lg">
               {teamPerformance?.displayTitle || 'Team Performance'}
            </span>
            <div className="flex overflow-hidden rounded bg-black/40">
               {teamPerformance?.tabs?.map((tab) => (
                  <button
                     key={tab.id}
                     className={`px-3 md:px-4 py-1 font-medium transition ${
                        selectedTab === tab.id
                           ? 'bg-[#232228] text-white shadow'
                           : 'bg-transparent text-gray-400 hover:bg-[#232228]'
                     }`}
                     onClick={() => setSelectedTab(tab.id)}
                  >
                     {tab.name}
                  </button>
               ))}
            </div>
         </div>

         {currentTab?.highlights?.length !== 0 ? (
            <div className="grid flex-1 grid-cols-1 pt-0 md:grid-cols-2 md:gap-0">
               <div className="bg-gradient-to-br from-[#00996B]/10 to-[#78E8A2]/10 p-4 md:p-6 flex flex-col justify-between md:min-h-[220px] h-full">
                  <div className="flex gap-3 mb-2 md:mb-auto">
                     <TeamPerformanceSvg />
                     <span className="text-base font-semibold text-white md:text-lg">
                        {topCloser?.title}
                     </span>
                  </div>
                  <p className="mb-2 text-sm text-white/70 md:mb-3 md:min-h-16">
                     {topCloser?.description}
                  </p>
                  <div className="text-lg font-bold md:text-2xl">
                     {topCloser?.agent?.name}
                  </div>
               </div>
               <div className="bg-gradient-to-br from-[#9061DA]/10 to-[#FF6D99]/10 p-4 md:p-6 flex flex-col justify-between md:min-h-[220px] h-full">
                  <div className="flex gap-3 mb-2 md:mb-auto">
                     <StrugglingToConnectSvg />
                     <span className="text-base font-semibold text-white md:text-lg">
                        {strugglingToConnect?.title}
                     </span>
                  </div>
                  <p className="mb-2 text-sm text-white/70 md:mb-3 md:min-h-16">
                     {strugglingToConnect?.description}
                  </p>
                  <div className="text-lg font-bold md:text-2xl">
                     {strugglingToConnect?.agent?.name}
                  </div>
               </div>
            </div>
         ) : (
            <EmptyForwardFlowCard />
         )}
      </div>
   )
}

export default TeamPerformanceCard
